import { CalendarDays, CaseSensitive, Hash, ToggleLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { ButtonGroup } from '@/_shared/components/ui/button';
import { isFiniteNumber } from '@/_shared/lib/numeric';

import type { InsightCommit } from './insights/InsightPanel';
import { InsightPanel } from './insights/InsightPanel';
import { insightState } from './insights/lib/insight-fit';
import { withAttributeOperator, withAttributePatch, withAttributeValue } from './lib/conditions';
import { ATTRIBUTE_OPERATORS, isOperatorAllowed, operatorShape } from './lib/operators';
import { type ClauseSummary, summarizeAttribute, summarizeRaw } from './lib/summarize';
import { PredicateActions } from './PredicateActions';
import { ClauseEditor, ClauseText, PredicateClause } from './PredicateClause';
import { ReadOnlyClause } from './ReadOnlyClause';
import type { AttributePredicate, AttributeType, ConditionValue, OperatorValue, RowBounds, RowControl } from './types';

/** Per-type glyph shown against the attribute (the column's type, as an icon). */
const TYPE_ICON: Record<AttributeType, ReactNode> = {
  string: <CaseSensitive aria-hidden className="size-3.5 text-muted-foreground" />,
  number: <Hash aria-hidden className="size-3.5 text-muted-foreground" />,
  boolean: <ToggleLeft aria-hidden className="size-3.5 text-muted-foreground" />,
  timestamp: <CalendarDays aria-hidden className="size-3.5 text-muted-foreground" />,
};

/** The operator + value shown as one plain-English segment (static, non-interactive). */
function StaticClause({ summary }: { summary: ClauseSummary }) {
  return (
    <ClauseText>
      <span className="text-muted-foreground">{summary.operator}</span>
      {summary.value != null && <span>{summary.value}</span>}
    </ClauseText>
  );
}

/** An attribute predicate rendered as a sentence: a fixed target (type icon + path) + the operator/value
 *  clause. When `type` is known and the operator is one that type offers, the clause is an editable button
 *  (opens a popover with the operator select + value control) and a grounded control can open below.
 *  Otherwise — no Row (unknown `type`) or an operator the type doesn't offer — the clause renders read-only. */
export function AttributePredicateClause({
  predicate,
  type,
  control,
  sentence,
  label,
  bounds,
  nonNullFraction,
  onChange,
  onRemove,
  disabled = false,
  readOnly = false,
  hideLabel = false,
  defaultInsightOpen = false,
}: {
  predicate: AttributePredicate;
  /** The column type, from the leaf's Row. Absent ⇒ non-editable. */
  type?: AttributeType;
  /** The grounded editing control, from the leaf's Row. */
  control?: RowControl;
  /** The Row's sentence, if the backend attached one — preferred over the predicate's for read-only display. */
  sentence?: string;
  /** A human name for the column, when the agent or digest supplied one. Shown as a tooltip on the
   *  column segment — never INSTEAD of the path, which is what the rule compiles against and the only
   *  string the customer can correlate with their own warehouse. */
  label?: string;
  /** The column's observed min/max, forwarded to the value control. */
  bounds?: RowBounds;
  /** The column's fill rate, forwarded to the panel's grounding footnote. */
  nonNullFraction?: number;
  onChange: (next: AttributePredicate) => void;
  onRemove?: () => void;
  disabled?: boolean;
  /** Force the clause static (no popover, no grounded control) — the display-only tree passes this. */
  readOnly?: boolean;
  hideLabel?: boolean;
  /** Start with the grounded control panel open (stories / first-open flows). */
  defaultInsightOpen?: boolean;
}) {
  const editable = !readOnly && type != null && isOperatorAllowed(type, predicate.operator);
  const operators = type != null ? ATTRIBUTE_OPERATORS[type] : [];
  const shape = editable ? operatorShape(operators, predicate.operator) : 'none';
  const summary = type != null ? summarizeAttribute(predicate, type) : summarizeRaw(predicate);

  const [open, setOpen] = useState(defaultInsightOpen);
  const toggle = () => setOpen((o) => !o);

  // No button that opens nothing — but "nothing" means an EMPTY panel, not merely a widget-less one. A
  // column the probe measured has a fill rate worth stating even when no control fits the operator, so
  // `'grounding'` opens too. The stale case still keeps its toggle only while already OPEN: pulling it
  // out from under a reader would leave them no way to close what they opened.
  const insight = editable
    ? insightState(control, shape, predicate.operator, predicate.value ?? null, nonNullFraction)
    : 'none';
  // A stale chart normally keeps its toggle only while already OPEN. When the column carries a fill rate
  // the panel has a second thing to show, which survives the chart being withheld — so the toggle stays
  // reachable rather than stranding a measurement behind a button that is not there.
  const insightOpens =
    insight === 'control' ||
    insight === 'grounding' ||
    (insight === 'stale' && (open || isFiniteNumber(nonNullFraction)));

  // A non-editable leaf renders as a plain box (like the raw-SQL clause, minus the code glyph) showing its
  // display sentence — the Row's sentence if the backend attached one, else the predicate's, else (neither)
  // fall through to our derived summary below.
  const displaySentence = sentence ?? predicate.sentence;
  if (!editable && displaySentence) {
    return <ReadOnlyClause text={displaySentence} onRemove={onRemove} disabled={disabled} />;
  }

  const setOperator = (operator: string) =>
    onChange(withAttributeOperator(predicate, type!, operator as OperatorValue));
  const setValue = (next: ConditionValue) => onChange(withAttributeValue(predicate, next));
  const applyCommit = (patch: InsightCommit) => onChange(withAttributePatch(predicate, type!, patch));

  const icon = type != null ? TYPE_ICON[type] : null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {!hideLabel && <span className="pl-1 text-xs text-muted-foreground">Have property</span>}
      <div className="flex items-center gap-2">
        {/* The clause keeps its natural width (`w-max` fieldset) and scrolls inside this flex-1 track, so a
            long sentence never pushes the row actions off-screen. `p-1 -m-1` gives the button focus ring /
            shadow room on all sides (the scroll clip would otherwise cut it) without changing the layout. */}
        <div className="-m-1 scrollbar-none min-w-0 flex-1 overflow-x-auto p-1">
          <fieldset disabled={disabled} className="m-0 flex w-max border-0 p-0">
            <ButtonGroup>
              <ClauseText {...(label ? { tooltip: label, ariaLabel: `${predicate.path} — ${label}` } : {})}>
                {icon}
                <span>{predicate.path}</span>
              </ClauseText>
              {editable ? (
                <PredicateClause summary={summary}>
                  <ClauseEditor
                    operators={operators}
                    operator={predicate.operator}
                    onOperatorChange={setOperator}
                    operatorAriaLabel={`${predicate.path} operator`}
                    shape={shape}
                    value={predicate.value ?? null}
                    onValueChange={setValue}
                    valueAriaBase={predicate.path}
                    type={type}
                    bounds={bounds}
                  />
                </PredicateClause>
              ) : (
                <StaticClause summary={summary} />
              )}
            </ButtonGroup>
          </fieldset>
        </div>
        <PredicateActions
          insightShown={open}
          onToggleInsights={insightOpens ? toggle : undefined}
          onRemove={onRemove}
          disabled={disabled}
        />
      </div>
      {insight !== 'none' && open && (
        <InsightPanel
          control={control}
          shape={shape}
          operator={predicate.operator}
          value={predicate.value ?? null}
          onCommit={applyCommit}
          fieldPath={predicate.path}
          nonNullFraction={nonNullFraction}
          disabled={disabled}
        />
      )}
    </div>
  );
}
